import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { resolveMediaUrl } from '@/utils/mediaUrl';

// Reader roster review. Anyone can build a profile at /readers/profile; the
// roster is curated, so new profiles wait here until someone approves them.
// Below that, the "Become a reader" requests that came in before the profile
// screen existed, so the team can see who still hasn't been told it's there.

type AdminReader = {
  userId: string; name: string; email: string | null; photoUrl: string | null;
  rateCents: number; directorRateCents: number; coachRateCents: number;
  categories: string[]; experience: string; timezone: string | null;
  complete: boolean; approved: boolean; published: boolean; wantsListed: boolean;
  application?: { background: string[]; backgroundOther: string; yearsExperience: string; professionalExperience: string; readingExperience: string; strongReader: string } | null;
  approvedBy?: string | null;
  approvalRequestedAt: string | null; rejectedAt: string | null; createdAt: string | null; updatedAt: string | null;
};
type InterestRow = { userId: string; name: string; email: string; createdAt: string | null; hasProfile: boolean; approved: boolean; published: boolean };

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const dollars = (c: number) => (c ? `$${(c / 100).toFixed(0)}` : '—');

export function ReadersAdminPanel() {
  const { toast } = useToast();
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [readers, setReaders] = useState<AdminReader[] | null>(null);
  const [interest, setInterest] = useState<InterestRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [r, i] = await Promise.all([
      fetch(`/api/admin/readers?status=${status}`, { credentials: 'include' }).then(x => x.ok ? x.json() : { readers: [] }),
      fetch('/api/admin/reader-interest', { credentials: 'include' }).then(x => x.ok ? x.json() : { requests: [] }),
    ]);
    setReaders(r.readers || []);
    setInterest(i.requests || []);
  }, [status]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const act = async (userId: string, verb: 'approve' | 'reject') => {
    setBusy(userId + verb);
    try {
      const res = await fetch(`/api/admin/readers/${userId}/${verb}`, { method: 'POST', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Failed');
      toast({ title: verb === 'approve' ? (d.published ? 'Approved and listed' : 'Approved') : 'Rejected' });
      await load();
    } catch (e: any) {
      toast({ title: 'Could not update', description: e.message, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const waiting = (interest || []).filter(r => !r.hasProfile);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Reader profiles</CardTitle>
          <CardDescription>
            A completed application lists a reader automatically and you get the answers by email. Revoke here if someone shouldn't be on the platform; approve here only for someone who was revoked or never finished the application.
          </CardDescription>
          <div className="flex gap-2 pt-2">
            {(['pending', 'approved', 'rejected'] as const).map(s => (
              <Button key={s} size="sm" variant={status === s ? 'default' : 'outline'} onClick={() => setStatus(s)}>
                {s[0].toUpperCase() + s.slice(1)}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {!readers ? <Loader2 className="w-5 h-5 animate-spin" /> : readers.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing {status}.</p>
          ) : (
            <div className="space-y-3">
              {readers.map(r => (
                <div key={r.userId} className="flex gap-4 items-start rounded-lg border p-3">
                  {r.photoUrl ? (
                    <img src={resolveMediaUrl(r.photoUrl)} alt="" className="rounded-md object-cover flex-shrink-0" style={{ width: 56, height: 70 }} />
                  ) : <div className="rounded-md bg-muted flex-shrink-0" style={{ width: 56, height: 70 }} />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{r.name || '(no name)'}</span>
                      <span className="text-xs text-muted-foreground">{r.email}</span>
                      {r.published && <Badge>Listed</Badge>}
                      {r.wantsListed && !r.approved && <Badge variant="secondary">Asked to be listed</Badge>}
                      {!r.complete && <Badge variant="outline">Incomplete</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Reader {dollars(r.rateCents)} · Director {dollars(r.directorRateCents)} · Coach {dollars(r.coachRateCents)}
                      {r.timezone ? ` · ${r.timezone}` : ''}{r.categories.length ? ` · ${r.categories.join(', ')}` : ''}
                      {r.approvalRequestedAt ? ` · asked ${fmt(r.approvalRequestedAt)}` : r.createdAt ? ` · started ${fmt(r.createdAt)}` : ''}
                    </div>
                    {r.experience && <p className="text-sm mt-1 line-clamp-3">{r.experience}</p>}
                    {r.application && (
                      <details className="mt-2 text-sm">
                        <summary className="cursor-pointer text-muted-foreground">Application{r.approvedBy === 'application' ? ' · listed on completion' : ''}</summary>
                        <div className="mt-2 space-y-1">
                          <div><span className="text-muted-foreground">Background:</span> {[...r.application.background, r.application.backgroundOther].filter(Boolean).join(', ') || '—'}</div>
                          <div><span className="text-muted-foreground">Years:</span> {r.application.yearsExperience || '—'}</div>
                          <div><span className="text-muted-foreground">Professional experience:</span> {r.application.professionalExperience || '—'}</div>
                          <div><span className="text-muted-foreground">Reading experience:</span> {r.application.readingExperience || '—'}</div>
                          <div><span className="text-muted-foreground">Strong reader:</span> {r.application.strongReader || '—'}</div>
                        </div>
                      </details>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {!r.approved && <Button size="sm" disabled={!!busy} onClick={() => act(r.userId, 'approve')}>{busy === r.userId + 'approve' ? '…' : 'Approve'}</Button>}
                    {!r.rejectedAt && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act(r.userId, 'reject')}>{busy === r.userId + 'reject' ? '…' : r.approved ? 'Revoke' : 'Reject'}</Button>}
                    {r.rejectedAt && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act(r.userId, 'approve')}>Approve after all</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Asked to become a reader{waiting.length ? ` · ${waiting.length} still without a profile` : ''}</CardTitle>
          <CardDescription>
            People who pressed "Become a reader" before the profile screen existed. The ones without a profile haven't been told they can set one up.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!interest ? <Loader2 className="w-5 h-5 animate-spin" /> : interest.length === 0 ? (
            <p className="text-sm text-muted-foreground">No requests.</p>
          ) : (
            <div className="space-y-1 text-sm">
              {interest.map(r => (
                <div key={r.userId + r.email} className="flex items-center gap-3 py-1 border-b last:border-0">
                  <span className="w-16 text-xs text-muted-foreground">{fmt(r.createdAt)}</span>
                  <span className="font-medium">{r.name || '—'}</span>
                  <span className="text-muted-foreground">{r.email}</span>
                  <span className="ml-auto">
                    {r.published ? <Badge>Listed</Badge> : r.approved ? <Badge variant="secondary">Approved</Badge> : r.hasProfile ? <Badge variant="outline">Profile started</Badge> : <Badge variant="destructive">No profile</Badge>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
